import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import authFetch from "../authFetch";
import PT from "../i18n/platformTranslations";
import { anchorDerivedColors } from "../utils/color";
import { buildDarkAccentCss, buildLightAccentCss } from "../utils/themeAccent";
import { COUNTRIES, CITIES_BY_COUNTRY, PROPERTY_TYPES } from "../utils/geo";
import { useValidationToast, validateFields } from "../utils/validation";
import { usePlatformThemeMode, tc, themeColors } from "./themeUtils";
import CommandNav from "./CommandNav";
import CountryCodePicker from "./CountryCodePicker";
import MoyasarPaymentInit from "./MoyasarPaymentInit";
import PlatformSearchSelect from "./PlatformSearchSelect";
import { getPlatform } from "../native";

/* ── sessionStorage-backed form state for signup persistence (survives refresh) ── */
function useSessionState(key, initial){
  const [val,setVal]=useState(()=>{try{const s=sessionStorage.getItem(key);return s?JSON.parse(s):initial;}catch{return initial;}});
  const set=useCallback((v)=>{setVal(prev=>{const next=typeof v==="function"?v(prev):v;try{sessionStorage.setItem(key,JSON.stringify(next));}catch{}return next;});},[key]);
  return[val,set];
}
export function clearSignupSession(){try{["cmd_signup_form","cmd_signup_step","cmd_signup_formStep","cmd_signup_pkg","cmd_signup_cycle","cmd_signup_invite","cmd_signup_emailVerified","cmd_signup_phoneVerified"].forEach(k=>sessionStorage.removeItem(k));}catch{}}

/* ── Validation helpers (shared client+server) ── */
const EMAIL_RE=/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const RESERVED_SUBDOMAINS=new Set(["admin","www","api","app","mail","ftp","staging","dev","test","dashboard","support","help","status","blog","docs","cdn"]);
function validatePasswordClient(pw){
  if(!pw||pw.length<8)return"Password must be at least 8 characters.";
  if(!/[a-z]/.test(pw))return"Password must include a lowercase letter.";
  if(!/[A-Z]/.test(pw))return"Password must include an uppercase letter.";
  if(!/[0-9]/.test(pw))return"Password must include a number.";
  return null;
}

/* iOS-only "signup unavailable" screen — shown instead of the payment wizard
   when the app is running on an iOS device. Text-only, no outbound links:
   Apple Guideline 3.1.3 prohibits buttons that "steer" users to external
   payment. Existing subscribers can still sign in from the landing page. */
function IosSignupBlocked({platform, onBack, lang}){
  const rtl=lang==="ar";
  const brandName=platform?.platformName||"Command";
  const webHost=(typeof window!=="undefined"&&window.location&&window.location.host)||"getcommand.app";
  return (
    <div dir={rtl?"rtl":"ltr"} style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:"24px",background:"var(--bg, #FAFAF9)"}}>
      <div style={{maxWidth:420,width:"100%",textAlign:"center"}}>
        <div style={{fontSize:22,fontWeight:700,color:"var(--ink)",marginBottom:12,letterSpacing:"-.01em"}}>
          {rtl?`مرحبًا بك في ${brandName}`:`Welcome to ${brandName}`}
        </div>
        <div style={{fontSize:15,color:"var(--g600)",lineHeight:1.55,marginBottom:24}}>
          {rtl
            ?`هذا التطبيق للأعضاء الحاليين. لإنشاء حساب جديد، قم بزيارة ${webHost} من المتصفح.`
            :`This app is for existing ${brandName} subscribers. To create a new account, visit ${webHost} from your web browser.`}
        </div>
        <div style={{fontSize:13,color:"var(--g500)",marginBottom:28,lineHeight:1.5}}>
          {rtl?"هل لديك حساب بالفعل؟":"Already have an account?"}
        </div>
        <button
          onClick={onBack}
          style={{width:"100%",padding:"14px 20px",fontSize:15,fontWeight:600,border:"1px solid var(--ink)",background:"var(--ink)",color:"#fff",borderRadius:12,cursor:"pointer",fontFamily:"inherit"}}
        >
          {rtl?"تسجيل الدخول":"Sign in"}
        </button>
      </div>
    </div>
  );
}

export default function PlatformSignup({platform, onCreated, onBack, onEnterWorkspace, lang, setLang, preselectedPkgId}){
  const pkgs=platform.platformPackages||[];
  /* Defensive gate: verification is only ENFORCED when the super admin
     has both turned the toggle on AND configured a provider that can
     actually deliver the code. This protects the signup flow from a
     half-configured state (toggle on, provider empty) that would
     otherwise 500 on /api/send-verification. The Registration &
     Verification panel shows a warning banner when either toggle is
     off so admins know what's happening. */
  const _notif=platform.platformNotifConfig||{};
  const _emailProviderReady=!!_notif.emailApiKey;
  const _phoneProviderReady=!!(_notif.twilioSid&&_notif.twilioToken&&_notif.twilioFrom);
  const requireEmailVerify=_notif.verifyEmail===true&&_emailProviderReady;
  const requirePhoneVerify=_notif.verifyPhone===true&&_phoneProviderReady;
  const allowedCycles=platform.allowedBillingCycles||"both";
  const hasTrial=(platform.globalTrialDays||0)>0;
  const preselected=preselectedPkgId&&pkgs.find(p=>p.id===preselectedPkgId);
  const skipPkgSelect=!!preselected||pkgs.length<=1;
  const [step,setStep]=useSessionState("cmd_signup_step",skipPkgSelect?"form":(pkgs.length>0?"packages":"form"));
  const [formStep,setFormStep]=useSessionState("cmd_signup_formStep",1); /* wizard: 1=account+profile, 2=workspace+household, 3=verify, 4=invite code */
  const [selectedPkg,setSelectedPkg]=useSessionState("cmd_signup_pkg",preselectedPkgId||pkgs[0]?.id||"");
  const [billingCycle,setBillingCycle]=useSessionState("cmd_signup_cycle",allowedCycles==="yearly"?"yearly":"monthly");
  const [form,setForm]=useSessionState("cmd_signup_form",{name:"",email:"",password:"",workspaceName:"",subdomain:"",phoneCode:"+966",phoneNumber:"",paymentId:"",promoCode:"",
    city:"",country:"Saudi Arabia",propertyType:"villa",residents:1,staffCount:0});
  const [inviteCode,setInviteCode]=useSessionState("cmd_signup_invite","");
  const [err,setErr]=useState("");
  const [verifyCode,setVerifyCode]=useState("");
  const [verifyPhoneCode,setVerifyPhoneCode]=useState("");
  const [verifySending,setVerifySending]=useState(false);
  const [creating,setCreating]=useState(false);
  const [verifyErr,setVerifyErr]=useState("");
  const [emailVerified,setEmailVerified]=useSessionState("cmd_signup_emailVerified",false);
  const [phoneVerified,setPhoneVerified]=useSessionState("cmd_signup_phoneVerified",false);
  const [promoErr,setPromoErr]=useState("");
  const [promoValid,setPromoValid]=useState(null); /* null | {trialDays, label} */
  const [showComparePlans,setShowComparePlans]=useState(false);
  const [showSignupPw,setShowSignupPw]=useState(false);
  const sf=(k,v)=>setForm(p=>({...p,[k]:v}));

  /* ── Auto-resume after Moyasar payment redirect ── */
  const paymentResumedRef=useRef(false);
  useEffect(()=>{
    if(paymentResumedRef.current)return;
    try{
      const storedPaymentId=sessionStorage.getItem("cmd_signup_paymentId");
      if(storedPaymentId&&form.name&&form.email&&form.password){
        paymentResumedRef.current=true;
        sessionStorage.removeItem("cmd_signup_paymentId");
        /* Form data is already restored from sessionStorage — finish signup with the payment ID */
        finishSignup(storedPaymentId);
      }
    }catch{}
  },[]);

  const t=PT[lang];
  const isRTL=lang==="ar";
  const vt=useValidationToast();
  /* Accent default aligned with the landing (violet primary from the
     iridescent gradient system). Admin override still wins. */
  const accent=platform.platformAccent||"#7c3aed";
  const theme=usePlatformThemeMode(platform);
  const C=themeColors(theme);
  const accentCss=theme==="dark"?buildDarkAccentCss(accent):buildLightAccentCss(accent);
  const shellCls=`cmd-landing ${theme==="dark"?"p-dark":"p-light"}${isRTL?" rtl":""}`;
  const shellFont={fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":"-apple-system,BlinkMacSystemFont,'SF Pro Display','SF Pro Text','Helvetica Neue',Helvetica,Arial,sans-serif"};

  /* ── Mobile detection (mirrors landing page) ── */
  const [isMobileSignup,setIsMobileSignup]=useState(()=>typeof window!=="undefined"&&window.innerWidth<=768);
  useEffect(()=>{const h=()=>setIsMobileSignup(window.innerWidth<=768);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  /* Mobile palette — same anchor-derived system as landing */
  const mAccent=platform.wsDefaultAnchorColor||accent;
  const {primary:mP,palette:mPal}=anchorDerivedColors(mAccent);
  const mat=(o)=>mAccent+Math.round(o*255).toString(16).padStart(2,"0");
  const mInk=theme==="dark"?mPal[10]:mP;
  const mBody=theme==="dark"?mPal[8]:mPal[4];
  const mMuted=theme==="dark"?mPal[6]:mPal[5];
  const mSubtle=theme==="dark"?mPal[4]:mPal[7];
  const mGrad=`linear-gradient(135deg,${theme==="dark"?mPal[5]:mPal[3]},${theme==="dark"?mPal[4]:mPal[2]})`;
  const mBtnTxt="#fff";
  const mSysFont=isRTL?"'Tajawal',-apple-system,sans-serif":"-apple-system,'SF Pro Display','SF Pro Text','Helvetica Neue',sans-serif";
  const mAccentCss=theme==="dark"?buildDarkAccentCss(mAccent):buildLightAccentCss(mAccent);

  const submit=async()=>{
    /* ── Required field checks ── */
    const m=validateFields([{ok:form.name.trim(),label:isRTL?"الاسم":"Name"},{ok:form.email.trim(),label:isRTL?"البريد":"Email"},{ok:form.password,label:isRTL?"كلمة المرور":"Password"},{ok:form.workspaceName.trim(),label:isRTL?"اسم مساحة العمل":"Workspace Name"},{ok:form.phoneNumber.trim(),label:isRTL?"الهاتف":"Phone Number"}]);
    if(m.length){vt.show(m.join(", "));setErr(t.allFields);return;}
    /* ── Email format validation ── */
    if(!EMAIL_RE.test(form.email.trim())){setErr(isRTL?"صيغة البريد الإلكتروني غير صحيحة":"Please enter a valid email address.");return;}
    /* ── Password strength validation ── */
    const pwErr=validatePasswordClient(form.password);
    if(pwErr){setErr(pwErr);return;}
    /* ── Phone validation ── */
    if(form.phoneNumber.replace(/\D/g,"").length<7){setErr(isRTL?"رقم الهاتف يجب أن يكون 7 أرقام على الأقل":"Phone number must be at least 7 digits.");return;}
    /* ── Optimistic client-side duplicate checks (server is authoritative) ── */
    const activeWs=(platform.workspaces||[]).filter(w=>w.status!=="deleted");
    if(activeWs.some(w=>(w.ownerEmail||"").toLowerCase()===form.email.trim().toLowerCase())){setErr("This email address is already registered.");return;}
    if(activeWs.some(w=>w.ownerPhone&&w.ownerPhone===form.phoneCode+form.phoneNumber.trim())){setErr("This phone number is already registered.");return;}
    if(platform.signupMode==="invite"){
      const code=(platform.inviteCodes||[]).find(c=>c.code===inviteCode.trim().toUpperCase()&&!c.usedAt);
      if(!code){setErr(t.invalidCode);return;}
    }
    /* Check payment — skip if trial period active, payments disabled, or promo applied */
    const selPkg=(platform.platformPackages||[]).find(p=>p.id===selectedPkg);
    const pkgPrice=billingCycle==="yearly"?(selPkg?.priceYearly||0):(selPkg?.priceMonthly||0);
    const needsPayment=selPkg&&pkgPrice>0&&!platform.paymentsDisabled&&!hasTrial&&!promoValid;
    if(needsPayment){
      /* ── Run ALL server-side validation BEFORE showing payment ── */
      setErr("");setCreating(true);
      try{
        const vResp=await authFetch("/api/signup",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
          name:form.name,email:form.email.trim(),password:form.password,
          phone:form.phoneCode+form.phoneNumber,
          workspaceName:form.workspaceName,
          packageId:selectedPkg||null,billingCycle,
          inviteCode:inviteCode.trim().toUpperCase(),
          promoCode:(form.promoCode||"").trim().toUpperCase(),
          validateOnly:true,
        })});
        const vResult=await vResp.json();
        if(!vResult.ok){
          setErr(vResult.error||"Validation failed. Please fix the errors and try again.");
          setCreating(false);return;
        }
      }catch(e){
        setErr("Network error. Please check your connection and try again.");
        setCreating(false);return;
      }
      setCreating(false);setStep("payment");return;
    }
    /* Verification check — only enforce if enabled in platform config */
    if(requireEmailVerify&&!emailVerified){setErr(isRTL?"يرجى التحقق من بريدك الإلكتروني أولاً":"Please verify your email first.");return;}
    if(requirePhoneVerify&&!phoneVerified){setErr(isRTL?"يرجى التحقق من رقم هاتفك أولاً":"Please verify your phone number first.");return;}
    await finishSignup();
  };

  const afterPayment=async(paymentId)=>{
    /* Payment ID is passed directly — no stale-state race condition */
    await finishSignup(paymentId);
  };

  const finishSignup=async(paymentId)=>{
    setErr("");setCreating(true);setStep("creating");
    try{
      const payload={
        name:form.name,email:form.email.trim(),password:form.password,
        phone:form.phoneCode+form.phoneNumber,
        workspaceName:form.workspaceName,
        packageId:selectedPkg||null,billingCycle,
        inviteCode:inviteCode.trim().toUpperCase(),
        promoCode:(form.promoCode||"").trim().toUpperCase(),
        householdData:{city:form.city,country:form.country,propertyType:form.propertyType,residents:form.residents,staffCount:form.staffCount},
        paymentId:paymentId||form.paymentId||null,
      };
      /* ── Call server-side signup endpoint (authoritative) ── */
      const resp=await authFetch("/api/signup",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(payload)});
      const result=await resp.json();
      if(!result.ok){
        if(result.pending){setStep("pending");return;}
        setErr(result.error||"Signup failed. Please try again.");
        setStep("form");
        return;
      }
      /* Server created the workspace — pass to parent for platform state update */
      await onCreated(result);
      clearSignupSession();
      /* Brief pause on "Creating" screen then auto-enter workspace */
      setTimeout(()=>{if(onEnterWorkspace)onEnterWorkspace();},2000);
    }catch(e){
      console.error("[finishSignup] workspace creation failed:",e);
      setErr("Something went wrong creating your workspace. Please try again.");
    }finally{setCreating(false);}
  };

  const submitVerifyEmail=async()=>{
    if(!verifyCode.trim()){setVerifyErr("Enter the 6-digit code.");return;}
    setVerifyErr("");
    try{
      const r=await authFetch("/api/send-verification",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:form.email.trim(),code:verifyCode.trim()})});
      const d=await r.json();
      if(!d.ok){setVerifyErr(d.error||"Verification failed.");return;}
      if(!d.valid){setVerifyErr("Incorrect or expired code. Try again.");return;}
      setEmailVerified(true);
      if(phoneVerified) await finishSignup();
    }catch{setVerifyErr("Network error. Please try again.");}
  };
  const submitVerifyPhone=async()=>{
    if(!verifyPhoneCode.trim()){setVerifyErr(isRTL?"أدخل رمز التحقق":"Enter the verification code.");return;}
    setVerifyErr("");
    try{
      const phone=form.phoneCode+form.phoneNumber.trim();
      const r=await authFetch("/api/verify-phone",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({phone,code:verifyPhoneCode.trim()})});
      const d=await r.json();
      if(!d.ok||!d.valid){setVerifyErr(d.reason||"Incorrect code. Try again.");return;}
      setPhoneVerified(true);
    }catch{setVerifyErr("Network error. Please try again.");}
  };

  const themedNav=(
    <CommandNav
      logoSrc={platform.platformLogo||"/icon.svg"}
      logoAlt={platform.platformName||"Command"}
      lang={lang}
      setLang={setLang}
      onLogoClick={onBack}
      onBack={onBack}
      backText={t.back}
      showNavLinks={false}
      showSignIn={false}
      showSignUp={false}
    />
  );

  /* ── Package selection ── */
  /* ── Wizard step helpers (hoisted for mobile + desktop access) ── */
  const totalSteps=platform.signupMode==="invite"?4:3;
  const wizStepTitles=isRTL
    ?["أنشئ حسابك","تفاصيل مساحة العمل",...(platform.signupMode==="invite"?["رمز الدعوة"]:[])  ]
    :["Create your account","Workspace details",...(platform.signupMode==="invite"?["Invite code"]:[])];
  const wizStepSubs=isRTL
    ?["أدخل بياناتك الشخصية.","أخبرنا عن مساحة عملك.",...(platform.signupMode==="invite"?["أدخل رمز الدعوة"]:[])  ]
    :["Enter your personal details.","Tell us about your workspace.",...(platform.signupMode==="invite"?["Enter your invite code"]:[])];
  const selPkgObj=pkgs.find(p=>p.id===selectedPkg);
  const nextFormStep=async()=>{
    if(formStep===1){
      if(!form.name.trim()||!form.email.trim()||!form.password||!form.phoneNumber.trim()){setErr(isRTL?"جميع الحقول مطلوبة":"All fields are required.");return;}
      if(!EMAIL_RE.test(form.email.trim())){setErr(isRTL?"صيغة البريد الإلكتروني غير صحيحة":"Please enter a valid email address.");return;}
      const pwErr=validatePasswordClient(form.password);
      if(pwErr){setErr(pwErr);return;}
      if(form.password!==(form.confirmPassword||"")){setErr(isRTL?"كلمات المرور غير متطابقة":"Passwords do not match.");return;}
      if(form.phoneNumber.replace(/\D/g,"").length<7){setErr(isRTL?"رقم الهاتف يجب أن يكون 7 أرقام على الأقل":"Phone number must be at least 7 digits.");return;}
      if(requireEmailVerify&&!emailVerified){setErr(isRTL?"يرجى التحقق من بريدك الإلكتروني":"Please verify your email address.");return;}
      if(requirePhoneVerify&&!phoneVerified){setErr(isRTL?"يرجى التحقق من رقم هاتفك":"Please verify your phone number.");return;}
      const activeWs=(platform.workspaces||[]).filter(w=>w.status!=="deleted");
      if(activeWs.some(w=>(w.ownerEmail||"").toLowerCase()===form.email.trim().toLowerCase())){setErr("This email is already registered.");return;}
      const phoneNorm=form.phoneCode+form.phoneNumber.trim();
      if(activeWs.some(w=>w.ownerPhone&&w.ownerPhone===phoneNorm)){setErr("This phone number is already registered.");return;}
      setErr("");setCreating(true);
      try{
        const r=await authFetch("/api/signup",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:form.email.trim(),phone:phoneNorm,emailCheckOnly:true})});
        const d=await r.json().catch(()=>({}));
        if(!r.ok){setErr(d.error||"This email or phone is already registered.");setCreating(false);return;}
      }catch(e){}
      setCreating(false);setFormStep(2);
    } else if(formStep===2){
      if(!form.workspaceName.trim()){setErr(isRTL?"أدخل اسم مساحة العمل":"Enter workspace name.");return;}
      if(platform.signupMode==="invite"){setErr("");setFormStep(3);return;}
      setErr("");submit();
    } else if(formStep===3){
      const code=(platform.inviteCodes||[]).find(c=>c.code===inviteCode.trim().toUpperCase()&&!c.usedAt);
      if(!code){setErr(t.invalidCode);return;}
      setErr("");submit();
    }
  };
  const inputIcon=(type)=>{
    const c=isMobileSignup?mMuted:C.muted;
    if(type==="email")return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.3"><rect x="2" y="3.5" width="12" height="9" rx="1.5"/><path d="M2 5l6 4 6-4"/></svg>;
    if(type==="lock")return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.3"><rect x="3.5" y="7" width="9" height="6" rx="1.5"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2"/></svg>;
    if(type==="user")return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.3"><circle cx="8" cy="5" r="2.5"/><path d="M3 14c0-2.8 2.2-5 5-5s5 2.2 5 5"/></svg>;
    if(type==="phone")return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.3"><rect x="4" y="1" width="8" height="14" rx="1.5"/><line x1="6" y1="12" x2="10" y2="12"/></svg>;
    if(type==="building")return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.3"><rect x="2" y="3" width="12" height="11" rx="1"/><line x1="5" y1="6" x2="7" y2="6"/><line x1="9" y1="6" x2="11" y2="6"/><line x1="5" y1="9" x2="7" y2="9"/><line x1="9" y1="9" x2="11" y2="9"/><rect x="6" y="11" width="4" height="3"/></svg>;
    if(type==="link")return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.3"><path d="M6.5 9.5l3-3M5 10.5a2.5 2.5 0 010-3.5l1-1M11 5.5a2.5 2.5 0 010 3.5l-1 1"/></svg>;
    if(type==="key")return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={c} strokeWidth="1.3"><circle cx="5.5" cy="6.5" r="2.5"/><path d="M7.5 8.5l5 5M10.5 11.5l2-2"/></svg>;
    return null;
  };
  const inpStyle={paddingInlineStart:38};

  /* ── Moyasar callback URL: redirect back to SPA with payment info ── */
  /* Form data is already in sessionStorage. Moyasar appends id=&status= to the URL. */
  const paymentCallbackUrl=useMemo(()=>{
    return window.location.origin+"/?signup_payment=1";
  },[]);

  /* iOS: App Store Guideline 3.1.1 forbids selling digital subscriptions
     through any payment path other than StoreKit IAP. Since we sell via
     Moyasar on web + Android, we show a sign-in-only screen on iOS and
     direct new users to the web. No outbound buttons: Apple treats
     "Subscribe on our website" links as steering (Guideline 3.1.3) and
     will reject. Plain text is fine. */
  if(getPlatform()==="ios"){
    return <IosSignupBlocked platform={platform} onBack={onBack} lang={lang} />;
  }

  /* ══════════════════════════════════════════════════════════════
     MOBILE SIGNUP — glass card continuation of mobile landing
  ══════════════════════════════════════════════════════════════ */
  if(isMobileSignup){
    const mShell=(children)=>(
      <div className={`cmd-landing ${shellCls} pml-wrap`}>
        <style>{`\nbody{background:${theme==="dark"?"#08080f":"#FAFAF9"};overflow:hidden;margin:0;}`}</style>
        {mAccentCss&&<style>{mAccentCss}</style>}
        <div className={tc(theme,"p-dark-blob")}/>
        <div className="p-hero-grid" style={{position:"fixed"}}/>
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"16px 20px",overflowY:"auto",WebkitOverflowScrolling:"touch"}}>
          {children}
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
    const mInp=tc(theme,"p-inp-dark");
    const mBtn=(label,onClick,opts={})=>(
      <button className="pml-btn" onClick={onClick} disabled={opts.disabled}
        style={{background:opts.ghost?theme==="dark"?"rgba(255,255,255,.08)":"rgba(0,0,0,.04)":"var(--accent-grad)",
          color:opts.ghost?mBody:"#fff",
          border:opts.ghost?`1px solid ${theme==="dark"?"rgba(255,255,255,.12)":"rgba(0,0,0,.06)"}`:"none",
          borderRadius:14,fontSize:15,fontWeight:600,padding:"14px 24px",cursor:"pointer",width:"100%",
          backdropFilter:opts.ghost?"blur(8px)":"none",WebkitBackdropFilter:opts.ghost?"blur(8px)":"none",
          boxShadow:opts.ghost?"none":"0 6px 22px -4px color-mix(in srgb, var(--accent-a) 50%, transparent)",opacity:opts.disabled?.6:1,
          transition:"all .15s cubic-bezier(.2,.9,.3,1)",marginTop:opts.mt||0}}>{label}</button>
    );
    const mBack=(onClick)=>(
      <button onClick={onClick} style={{background:"none",border:"none",color:mMuted,fontSize:13,cursor:"pointer",padding:"10px 0",width:"100%",textAlign:"center"}}>{isRTL?"رجوع":"Back"}</button>
    );
    const mProgress=(current,total)=>(
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:16}}>
        {Array.from({length:total}).map((_,i)=>(
          <div key={i} style={{height:3,borderRadius:2,flex:1,background:i<current?"var(--accent-grad)":(theme==="dark"?"rgba(255,255,255,.1)":"rgba(0,0,0,.06)"),boxShadow:i<current?"0 1px 6px -2px color-mix(in srgb, var(--accent-a) 45%, transparent)":"none",transition:"background .25s, box-shadow .25s"}}/>
        ))}
        <span style={{fontSize:11,color:mMuted,fontFamily:"'SF Mono',ui-monospace,Menlo,Monaco,monospace",letterSpacing:"0.08em",flexShrink:0}}>{current}/{total}</span>
      </div>
    );

    /* ── PACKAGES ── */
    if(step==="packages") return mShell(
      <div className="pml-glass" style={{maxHeight:"85vh",overflowY:"auto"}}>
        {vt.Toast}
        <h2 style={{fontFamily:mSysFont,fontSize:22,fontWeight:700,color:mInk,marginBottom:6,textAlign:"center"}}>{isRTL?<>اختر <span className="grad-text">خطتك</span></>:<>Choose your <span className="grad-text">plan</span></>}</h2>
        <p style={{fontSize:13,color:mMuted,textAlign:"center",marginBottom:12}}>{isRTL?"اختر الباقة المناسبة لك":"Select the package that fits your needs"}</p>
        {(platform.globalTrialDays||0)>0&&(()=>{
          const d=platform.globalTrialDays;
          const trialLabel=d>=30?`${Math.round(d/30)}-month`:`${d}-day`;
          const trialLabelAr=d>=30?`${Math.round(d/30)} ${Math.round(d/30)===1?"شهر":"أشهر"}`:`${d} ${d===1?"يوم":"أيام"}`;
          return(
            <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",justifyContent:"center",padding:"6px 12px",borderRadius:999,border:"1px solid color-mix(in srgb, var(--accent-a) 24%, transparent)",background:"color-mix(in srgb, var(--accent-a) 8%, transparent)",marginBottom:12,fontSize:11,fontWeight:500,color:mBody}}>
              <span className="grad-text" style={{fontWeight:700}}>
                {isRTL?`تجربة مجانية ${trialLabelAr}`:`${trialLabel} free trial`}
              </span>
              <span style={{color:mMuted}}>·</span>
              <span>{isRTL?"بدون بطاقة":"No card"}</span>
              <span style={{color:mMuted}}>·</span>
              <span>{isRTL?"ألغِ في أي وقت":"Cancel anytime"}</span>
            </div>
          );
        })()}
        {allowedCycles==="both"&&pkgs.some(p=>(p.priceMonthly||0)>0||(p.priceYearly||0)>0)&&(
          <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
            <div style={{display:"inline-flex",background:theme==="dark"?"rgba(255,255,255,.06)":"rgba(0,0,0,.04)",borderRadius:20,padding:2,gap:2}}>
              {["monthly","yearly"].map(c=>(
                <button key={c} onClick={()=>setBillingCycle(c)}
                  style={{padding:"6px 16px",borderRadius:18,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,
                    background:billingCycle===c?"var(--accent-grad)":"transparent",color:billingCycle===c?"#fff":mMuted,boxShadow:billingCycle===c?"0 2px 8px -2px color-mix(in srgb, var(--accent-a) 45%, transparent)":"none",transition:"all .15s"}}>{c==="monthly"?(isRTL?"شهري":"Monthly"):(isRTL?"سنوي":"Annual")}</button>
              ))}
            </div>
          </div>
        )}
        {pkgs.map(pkg=>{
          const sel=selectedPkg===pkg.id;const locked=!!pkg.inviteOnly;
          const priceVal=billingCycle==="yearly"?(pkg.priceYearly||0):(pkg.priceMonthly||0);
          const hasPricing=(pkg.priceMonthly||0)>0||(pkg.priceYearly||0)>0;
          const mn=(platform.platformModuleNames||{});
          return(
            <div key={pkg.id} className={sel?"cmd-pkg-highlight":undefined} onClick={()=>{if(!locked)setSelectedPkg(pkg.id);}}
              style={{background:sel?"color-mix(in srgb, var(--accent-a) 8%, transparent)":(theme==="dark"?"rgba(255,255,255,.03)":"rgba(0,0,0,.02)"),
                border:sel?undefined:`1.5px solid ${locked?"rgba(120,80,200,.3)":(theme==="dark"?"rgba(255,255,255,.08)":"rgba(0,0,0,.05)")}`,
                borderRadius:14,padding:"16px 14px",marginBottom:10,cursor:locked?"default":"pointer",opacity:locked?.6:1,position:"relative",boxShadow:sel?"0 6px 22px -8px color-mix(in srgb, var(--accent-a) 45%, transparent)":"none",transition:"all .15s"}}>
              {pkg.highlight&&!locked&&<div style={{position:"absolute",top:-9,left:"50%",transform:"translateX(-50%)",background:"var(--accent-grad)",color:"#fff",fontSize:9,fontWeight:700,padding:"2px 10px",borderRadius:20,whiteSpace:"nowrap",letterSpacing:"0.04em",boxShadow:"0 2px 8px -2px color-mix(in srgb, var(--accent-a) 50%, transparent)"}}>{isRTL?"الأكثر شيوعاً":"POPULAR"}</div>}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:18,height:18,borderRadius:"50%",border:sel?"none":`2px solid ${theme==="dark"?"rgba(255,255,255,.2)":"rgba(0,0,0,.12)"}`,background:sel?"var(--accent-grad)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:sel?"0 2px 8px -2px color-mix(in srgb, var(--accent-a) 50%, transparent)":"none"}}>
                    {sel&&<div style={{width:7,height:7,borderRadius:"50%",background:"#fff"}}/>}
                  </div>
                  {sel?(
                    <span className="grad-text" style={{fontSize:15,fontWeight:700}}>{pkg.name}</span>
                  ):(
                    <span style={{fontSize:15,fontWeight:700,color:mBody}}>{pkg.name}</span>
                  )}
                </div>
                {hasPricing&&<span style={{fontSize:16,fontWeight:700,color:mInk,fontFamily:"'SF Mono',ui-monospace,Menlo,Monaco,monospace"}}>{priceVal===0?(isRTL?"مجاني":"Free"):`${priceVal} SAR`}{priceVal>0&&<span style={{fontSize:10,color:mMuted}}>/{billingCycle==="yearly"?(isRTL?"سنة":"yr"):(isRTL?"شهر":"mo")}</span>}</span>}
              </div>
              <div style={{fontSize:12,color:mMuted,lineHeight:1.5,marginBottom:6}}>{pkg.description}</div>
              {pkg.showModules!==false&&(pkg.modules||[]).length>0&&(
                <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                  {(pkg.modules||[]).slice(0,5).map(m=>(<span key={m} style={{fontSize:9,padding:"2px 7px",borderRadius:10,background:"linear-gradient(135deg, color-mix(in srgb, var(--accent-a) 14%, transparent), color-mix(in srgb, var(--accent-b) 10%, transparent))",color:"var(--accent-a)",fontWeight:600,border:"1px solid color-mix(in srgb, var(--accent-a) 14%, transparent)"}}>{mn[m]||m}</span>))}
                  {(pkg.modules||[]).length>5&&<span style={{fontSize:9,color:mMuted}}>+{(pkg.modules||[]).length-5}</span>}
                </div>
              )}
            </div>
          );
        })}
        <div style={{marginTop:8}}>
          {mBtn(isRTL?"متابعة":"Continue",()=>selectedPkg&&setStep("form"),{disabled:!selectedPkg})}
          {mBack(onBack)}
        </div>
      </div>
    );

    /* ── FORM WIZARD ── */
    if(step==="form") return mShell(
      <div className="pml-glass" style={{maxHeight:"85vh",overflowY:"auto"}}>
        {vt.Toast}
        {mProgress(formStep,totalSteps)}
        <h2 style={{fontFamily:mSysFont,fontSize:20,fontWeight:700,color:mInk,marginBottom:4}}>{wizStepTitles[formStep-1]}</h2>
        <p style={{fontSize:13,color:mMuted,marginBottom:16}}>{wizStepSubs[formStep-1]}</p>
        {err&&<div style={{background:"rgba(220,50,50,.1)",border:"1px solid rgba(220,50,50,.2)",borderRadius:10,padding:"10px 12px",fontSize:12,color:"#ff7070",marginBottom:12}}>{err}</div>}

        {/* Step 1: Account */}
        {formStep===1&&(<>
          <div style={{marginBottom:10,position:"relative"}}>
            <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("user")}</div>
            <input className={mInp} value={form.name} onChange={e=>sf("name",e.target.value)} placeholder={isRTL?"الاسم الكامل":"Full name"} style={inpStyle} autoFocus/>
          </div>
          <div style={{marginBottom:10}}>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <div style={{flex:1,position:"relative"}}>
                <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("email")}</div>
                <input className={mInp} type="email" value={form.email} onChange={e=>{sf("email",e.target.value);setEmailVerified(false);}} placeholder={isRTL?"البريد الإلكتروني":"Email address"} style={inpStyle} disabled={emailVerified}/>
              </div>
              {requireEmailVerify&&!emailVerified&&!verifySending&&form.email.includes("@")&&(
                <button onClick={async()=>{setVerifyErr("");setVerifySending(true);try{const r=await authFetch("/api/send-verification",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:form.email.trim()})});const d=await r.json();if(!d.ok)setVerifyErr(d.error||"Failed");setVerifySending(false);}catch{setVerifyErr("Network error");setVerifySending(false);}}}
                  style={{background:mAccent,color:mBtnTxt,border:"none",borderRadius:8,padding:"10px 12px",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{isRTL?"تحقق":"Verify"}</button>
              )}
              {requireEmailVerify&&emailVerified&&<span style={{color:"#2D7D52",fontSize:12,fontWeight:700,whiteSpace:"nowrap"}}>✓</span>}
            </div>
            {verifySending&&<div style={{fontSize:10,color:mAccent,marginTop:3}}>{isRTL?"جاري إرسال الرمز...":"Sending code..."}</div>}
            {requireEmailVerify&&!emailVerified&&form.email.includes("@")&&!verifySending&&verifyErr&&<div style={{fontSize:10,color:"#D94F3D",marginTop:3}}>{verifyErr}</div>}
            {requireEmailVerify&&!emailVerified&&!verifySending&&form.email.includes("@")&&(
              <div style={{display:"flex",gap:6,marginTop:5,alignItems:"center"}}>
                <input className={mInp} value={verifyCode} onChange={e=>setVerifyCode(e.target.value.replace(/\D/g,""))} maxLength={6} placeholder={isRTL?"رمز التحقق":"6-digit code"} style={{flex:1,fontSize:12,padding:"8px 10px",textAlign:"center",letterSpacing:4,fontFamily:"'JetBrains Mono',monospace"}}/>
                {verifyCode.length>=4&&(
                  <button onClick={submitVerifyEmail} style={{background:mAccent,color:mBtnTxt,border:"none",borderRadius:8,padding:"8px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>{isRTL?"تأكيد":"Confirm"}</button>
                )}
              </div>
            )}
          </div>
          <div style={{marginBottom:10}}>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <CountryCodePicker value={form.phoneCode} onChange={v=>sf("phoneCode",v)} theme={theme}/>
              <input className={mInp} type="tel" value={form.phoneNumber} onChange={e=>{sf("phoneNumber",e.target.value.replace(/\D/g,""));setPhoneVerified(false);}} placeholder={isRTL?"رقم الهاتف":"Phone number"} style={{flex:1}} disabled={phoneVerified}/>
              {requirePhoneVerify&&!phoneVerified&&form.phoneNumber.length>=7&&(
                <button onClick={async()=>{setVerifyErr("");try{const r=await authFetch("/api/verify-phone",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({phone:form.phoneCode+form.phoneNumber.trim()})});const d=await r.json();if(!d.ok)setVerifyErr(d.error||"Failed");}catch{setVerifyErr("Network error.");}}}
                  style={{background:mAccent,color:mBtnTxt,border:"none",borderRadius:8,padding:"10px 12px",fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{isRTL?"تحقق":"Verify"}</button>
              )}
              {requirePhoneVerify&&phoneVerified&&<span style={{color:"#2D7D52",fontSize:12,fontWeight:700}}>✓</span>}
            </div>
            {requirePhoneVerify&&!phoneVerified&&form.phoneNumber.length>=7&&(
              <div style={{display:"flex",gap:6,marginTop:5,alignItems:"center"}}>
                <input className={mInp} value={verifyPhoneCode} onChange={e=>setVerifyPhoneCode(e.target.value.replace(/\D/g,""))} maxLength={6} placeholder={isRTL?"رمز الواتساب":"WhatsApp code"} style={{flex:1,fontSize:12,padding:"8px 10px",textAlign:"center",letterSpacing:4,fontFamily:"'JetBrains Mono',monospace"}}/>
                {verifyPhoneCode.length>=4&&(
                  <button onClick={submitVerifyPhone} style={{background:mAccent,color:mBtnTxt,border:"none",borderRadius:8,padding:"8px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}}>{isRTL?"تأكيد":"Confirm"}</button>
                )}
              </div>
            )}
          </div>
          <div style={{marginBottom:10,position:"relative"}}>
            <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",zIndex:1}}>{inputIcon("lock")}</div>
            <input className={mInp} type={showSignupPw?"text":"password"} value={form.password} onChange={e=>sf("password",e.target.value)} placeholder={isRTL?"كلمة المرور":"Password"} style={{...inpStyle,paddingInlineEnd:44}}/>
            <button onClick={()=>setShowSignupPw(p=>!p)} style={{position:"absolute",insetInlineEnd:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:4,color:mMuted}}>
              {showSignupPw?<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/></svg>
              :<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/><line x1="3" y1="3" x2="17" y2="17"/></svg>}
            </button>
          </div>
          <div style={{marginBottom:14,position:"relative"}}>
            <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none",zIndex:1}}>{inputIcon("lock")}</div>
            <input className={mInp} type={showSignupPw?"text":"password"} value={form.confirmPassword||""} onChange={e=>sf("confirmPassword",e.target.value)} placeholder={isRTL?"تأكيد كلمة المرور":"Confirm password"} style={{...inpStyle,paddingInlineEnd:44}} onKeyDown={e=>e.key==="Enter"&&nextFormStep()}/>
            <button onClick={()=>setShowSignupPw(p=>!p)} style={{position:"absolute",insetInlineEnd:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",padding:4,color:mMuted}}>
              {showSignupPw?<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/></svg>
              :<svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="3"/><line x1="3" y1="3" x2="17" y2="17"/></svg>}
            </button>
          </div>
          {verifyErr&&<div style={{fontSize:10,color:"#D94F3D",marginBottom:6}}>{verifyErr}</div>}
        </>)}

        {/* Step 2: Workspace */}
        {formStep===2&&(<>
          <div style={{marginBottom:10,position:"relative"}}>
            <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("building")}</div>
            <input className={mInp} value={form.workspaceName} onChange={e=>sf("workspaceName",e.target.value)} placeholder={isRTL?"اسم مساحة العمل":"Workspace name"} style={inpStyle} autoFocus/>
          </div>
          <div style={{paddingTop:12,marginTop:4,borderTop:`1px solid ${theme==="dark"?"rgba(255,255,255,.06)":"rgba(0,0,0,.04)"}`}}>
            <div style={{fontSize:10,fontWeight:600,color:mMuted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>{isRTL?"تفاصيل المنزل":"Household Details"}</div>
            <div style={{display:"flex",gap:6,marginBottom:10}}>
              <PlatformSearchSelect value={form.country} onChange={v=>{sf("country",v);sf("city","");}} options={COUNTRIES.map(c=>({value:c,label:c}))} placeholder={isRTL?"الدولة":"Country"} theme={theme} accent={mAccent}/>
              <PlatformSearchSelect value={form.city} onChange={v=>sf("city",v)} options={(CITIES_BY_COUNTRY[form.country]||[]).map(c=>({value:c,label:c}))} placeholder={isRTL?"المدينة":"City"} theme={theme} accent={mAccent}/>
            </div>
            <div style={{marginBottom:10}}>
              <PlatformSearchSelect value={form.propertyType} onChange={v=>sf("propertyType",v)} options={PROPERTY_TYPES} placeholder={isRTL?"نوع العقار":"Property type"} theme={theme} accent={mAccent}/>
            </div>
            <div style={{display:"flex",gap:6,marginBottom:10}}>
              <div style={{flex:1}}>
                <label style={{fontSize:10,color:mMuted,marginBottom:3,display:"block"}}>{isRTL?"المقيمين":"Residents"}</label>
                <PlatformSearchSelect value={String(form.residents||1)} onChange={v=>sf("residents",parseInt(v)||1)} options={Array.from({length:20},(_,i)=>({value:String(i+1),label:String(i+1)}))} placeholder="#" theme={theme} accent={mAccent}/>
              </div>
              <div style={{flex:1}}>
                <label style={{fontSize:10,color:mMuted,marginBottom:3,display:"block"}}>{isRTL?"الموظفين":"Staff"}</label>
                <PlatformSearchSelect value={String(form.staffCount||0)} onChange={v=>sf("staffCount",parseInt(v)||0)} options={Array.from({length:21},(_,i)=>({value:String(i),label:String(i)}))} placeholder="#" theme={theme} accent={mAccent}/>
              </div>
            </div>
          </div>
          {selPkgObj&&pkgs.length>1&&(
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4}}>
              <span style={{fontSize:11,color:mMuted}}>{isRTL?"الخطة:":"Plan:"}</span>
              <span style={{fontSize:11,color:mAccent,fontWeight:600}}>{selPkgObj.name}</span>
              <button onClick={()=>setShowComparePlans(true)} style={{fontSize:10,color:mAccent,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>{isRTL?"قارن":"Compare"}</button>
            </div>
          )}
        </>)}

        {/* Step 3: Invite code */}
        {formStep===3&&(<>
          <div style={{marginBottom:16,position:"relative"}}>
            <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("key")}</div>
            <input className={mInp} value={inviteCode} onChange={e=>setInviteCode(e.target.value)} placeholder="XXXXXX" style={{...inpStyle,textTransform:"uppercase",letterSpacing:4,fontFamily:"'JetBrains Mono',monospace"}} autoFocus onKeyDown={e=>e.key==="Enter"&&nextFormStep()}/>
          </div>
        </>)}

        {/* Buttons */}
        <div style={{marginTop:8}}>
          {mBtn(creating?(isRTL?"جاري الإنشاء...":"Creating..."):formStep<totalSteps?(isRTL?"متابعة":"Continue"):(platform.signupMode==="approval"?t.requestWs:(isRTL?"إنشاء مساحة العمل":"Create workspace")),nextFormStep,{disabled:creating})}
          {formStep>1?mBack(()=>{setErr("");setFormStep(formStep-1);}):mBack(skipPkgSelect?onBack:()=>setStep("packages"))}
        </div>
        {/* Compare plans overlay */}
        {showComparePlans&&(
          <div onClick={()=>setShowComparePlans(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:9999,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:0}}>
            <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxHeight:"75vh",overflowY:"auto",background:theme==="dark"?"#161618":"white",borderRadius:"20px 20px 0 0",padding:"24px 20px max(env(safe-area-inset-bottom,20px),20px)"}}>
              <div style={{width:36,height:4,borderRadius:2,background:theme==="dark"?"rgba(255,255,255,.15)":"rgba(0,0,0,.1)",margin:"0 auto 16px"}}/>
              <h3 style={{fontFamily:mSysFont,fontSize:18,fontWeight:700,color:mInk,marginBottom:16}}>{isRTL?"قارن الخطط":"Compare Plans"}</h3>
              {pkgs.filter(p=>!p.inviteOnly).map(pkg=>{
                const isSel=selectedPkg===pkg.id;
                const priceVal=billingCycle==="yearly"?(pkg.priceYearly||0):(pkg.priceMonthly||0);
                return(
                  <div key={pkg.id} onClick={()=>{setSelectedPkg(pkg.id);setShowComparePlans(false);}}
                    style={{border:`1.5px solid ${isSel?mAccent:(theme==="dark"?"rgba(255,255,255,.08)":"rgba(0,0,0,.06)")}`,borderRadius:14,padding:"14px 12px",marginBottom:8,cursor:"pointer",background:isSel?mat(.06):"transparent"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <span style={{fontSize:14,fontWeight:700,color:mInk}}>{pkg.name}</span>
                      {priceVal>0&&<span style={{fontSize:14,fontWeight:700,color:mInk,fontFamily:"'JetBrains Mono',monospace"}}>{priceVal} SAR<span style={{fontSize:10,color:mMuted}}>/{billingCycle==="yearly"?"yr":"mo"}</span></span>}
                    </div>
                    <div style={{fontSize:11,color:mMuted,marginBottom:6}}>{pkg.description}</div>
                    {(pkg.modules||[]).map(m=>(
                      <span key={m} style={{fontSize:9,color:mAccent,marginInlineEnd:6}}>✓ {(platform.platformModuleNames||{})[m]||m}</span>
                    ))}
                    {isSel&&<div style={{fontSize:10,color:mAccent,fontWeight:700,marginTop:6}}>{isRTL?"الخطة الحالية":"Current plan"}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );

    /* ── PAYMENT ── */
    if(step==="payment"){
      const selPkg2=(platform.platformPackages||[]).find(p=>p.id===selectedPkg)||{};
      const pubKey=(platform.platformApiKeys||{}).moyasarPublishableKey||"";
      const priceVal=billingCycle==="yearly"?(selPkg2.priceYearly||0):(selPkg2.priceMonthly||0);
      const effectivePrice=promoValid?0:priceVal;
      const amountHalalas=Math.round(effectivePrice*100);
      const validatePromo=async()=>{
        if(!(form.promoCode||"").trim()){setPromoErr("");setPromoValid(null);return;}
        setPromoErr("");
        try{
          const r=await authFetch("/api/validate-promo",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({code:form.promoCode.trim()})});
          const d=await r.json();
          if(d.ok){setPromoValid({trialDays:d.trialDays,label:d.label});setPromoErr("");}
          else{setPromoValid(null);setPromoErr(d.error||(isRTL?"رمز ترويجي غير صالح":"Invalid promo code."));}
        }catch{setPromoErr(isRTL?"خطأ في الشبكة":"Network error.");}
      };
      return mShell(
        <div className="pml-glass" style={{maxHeight:"85vh",overflowY:"auto"}}>
          <h2 style={{fontFamily:mSysFont,fontSize:20,fontWeight:700,color:mInk,marginBottom:4}}>{isRTL?"الدفع":"Complete payment"}</h2>
          <div style={{fontSize:12,color:mMuted,marginBottom:14}}>
            {selPkg2.name} · <span style={{fontFamily:"'JetBrains Mono',monospace",color:mAccent}}>{priceVal} SAR</span> / {billingCycle==="yearly"?(isRTL?"سنة":"year"):(isRTL?"شهر":"month")}
          </div>
          <div style={{background:theme==="dark"?"rgba(255,255,255,.04)":"rgba(0,0,0,.02)",border:`1px solid ${theme==="dark"?"rgba(255,255,255,.08)":"rgba(0,0,0,.04)"}`,borderRadius:12,padding:"14px 12px",marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:600,color:mMuted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>{isRTL?"رمز ترويجي":"Promo code"}</div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <input className={mInp} value={form.promoCode||""} onChange={e=>{sf("promoCode",e.target.value.toUpperCase());setPromoValid(null);setPromoErr("");}}
                placeholder={isRTL?"أدخل الرمز":"Enter code"} style={{flex:1,fontSize:12,textTransform:"uppercase",letterSpacing:2,fontFamily:"'JetBrains Mono',monospace"}}/>
              {(form.promoCode||"").trim()&&!promoValid&&(
                <button onClick={validatePromo} style={{background:mAccent,color:mBtnTxt,border:"none",borderRadius:8,padding:"10px 12px",fontSize:11,fontWeight:600,cursor:"pointer"}}>{isRTL?"تطبيق":"Apply"}</button>
              )}
            </div>
            {promoValid&&<div style={{fontSize:11,color:"#2D7D52",marginTop:5,fontWeight:600}}>✓ {isRTL?"تم تطبيق العرض":"Promo applied"}</div>}
            {promoErr&&<div style={{fontSize:11,color:"#D94F3D",marginTop:5}}>{promoErr}</div>}
          </div>
          {promoValid?(
            <div style={{background:theme==="dark"?"rgba(45,125,82,.1)":"#F0FDF4",border:`1px solid ${theme==="dark"?"rgba(45,125,82,.2)":"#BBF7D0"}`,borderRadius:12,padding:20,textAlign:"center"}}>
              <div style={{fontSize:14,color:theme==="dark"?"#7be8a0":"#065F46",fontWeight:600,marginBottom:6}}>{isRTL?"لا يلزم الدفع":"No payment required"}</div>
              {mBtn(isRTL?"تفعيل الاشتراك":"Activate",()=>afterPayment(""),{mt:10})}
            </div>
          ):(
            <div style={{background:theme==="dark"?"rgba(255,255,255,.03)":"rgba(0,0,0,.02)",border:`1px solid ${theme==="dark"?"rgba(255,255,255,.06)":"rgba(0,0,0,.04)"}`,borderRadius:12,padding:16}}>
              {pubKey?(<>
                <div id="moyasar-payment-form" className="mysr-form"/>
                <MoyasarPaymentInit amount={amountHalalas} description={`${selPkg2.name} subscription`} pubKey={pubKey} onPaid={afterPayment} callbackUrl={paymentCallbackUrl}/>
              </>):(
                <div style={{textAlign:"center",padding:"12px 0",fontSize:12,color:mMuted}}>{isRTL?"بوابة الدفع غير مُعدّة":"Payment gateway not configured"}</div>
              )}
            </div>
          )}
          <div style={{fontSize:10,color:mSubtle,marginTop:10,textAlign:"center"}}>{isRTL?"مؤمّن بواسطة Moyasar · mada · Visa · Mastercard · Apple Pay":"Secured by Moyasar · mada · Visa · Mastercard · Apple Pay"}</div>
          {mBack(()=>setStep("form"))}
        </div>
      );
    }

    /* ── CREATING ── */
    if(step==="creating") return mShell(
      <div className="pml-glass" style={{textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px"}}>
        <div style={{width:44,height:44,border:`3px solid ${mAccent}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite",marginBottom:20}}/>
        <h2 style={{fontFamily:mSysFont,fontSize:20,fontWeight:700,color:mInk,marginBottom:8}}>{isRTL?"جاري إنشاء مساحة العمل...":"Creating your workspace..."}</h2>
        <p style={{fontSize:13,color:mMuted,lineHeight:1.6}}>{isRTL?"يرجى الانتظار":"Please wait while we set everything up"}</p>
      </div>
    );

    /* ── PENDING ── */
    if(step==="pending") return mShell(
      <div className="pml-glass" style={{textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px"}}>
        <div style={{fontSize:40,marginBottom:16}}>⏳</div>
        <h2 style={{fontFamily:mSysFont,fontSize:20,fontWeight:700,color:mInk,marginBottom:8}}>{t.requestSubmitted}</h2>
        <p style={{fontSize:13,color:mMuted,lineHeight:1.6,marginBottom:20}}>{t.pendingBody}</p>
        {mBtn(t.back,onBack,{ghost:true})}
      </div>
    );

    /* ── DONE ── */
    if(step==="done"){
      setTimeout(()=>{if(onEnterWorkspace)onEnterWorkspace();},1500);
      return mShell(
        <div className="pml-glass" style={{textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 24px"}}>
          <div style={{fontSize:40,marginBottom:16}}>✦</div>
          <h2 style={{fontFamily:mSysFont,fontSize:20,fontWeight:700,color:mInk,marginBottom:8}}>{t.wsCreated}</h2>
          <div style={{fontSize:12,color:mMuted,marginBottom:16}}>
            {isRTL?"جاري الدخول...":"Entering your workspace..."}
          </div>
          <div style={{width:28,height:28,border:`3px solid ${mAccent}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
        </div>
      );
    }
  }
  /* ══════ END MOBILE SIGNUP ══════ */

  if(step==="packages") return(
    <div className={shellCls} style={{...shellFont,overflowY:"auto",minHeight:"100vh"}}>
      
      {accentCss&&<style>{accentCss}</style>}
      <div className={tc(theme,"p-dark-blob")}/>
      {themedNav}
      <div style={{maxWidth:860,margin:"0 auto",padding:"48px 24px 64px"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:30,color:C.text,fontStyle:"italic",marginBottom:8}}>{isRTL?"اختر خطتك":"Choose your plan"}</div>
          <div style={{fontSize:14,color:C.muted,marginBottom:16}}>{isRTL?"اختر الباقة المناسبة لك. يمكنك تغييرها لاحقاً":"Select the package that fits your needs. You can change it later."}</div>
          {(platform.globalTrialDays||0)>0&&(()=>{
            const d=platform.globalTrialDays;
            const trialLabel=d>=30?`${Math.round(d/30)}-month`:`${d}-day`;
            const trialLabelAr=d>=30?`${Math.round(d/30)} ${Math.round(d/30)===1?"شهر":"أشهر"}`:`${d} ${d===1?"يوم":"أيام"}`;
            return(
              <div style={{display:"inline-flex",alignItems:"center",gap:10,flexWrap:"wrap",justifyContent:"center",padding:"8px 16px",borderRadius:999,border:"1px solid color-mix(in srgb, var(--accent-a) 24%, transparent)",background:"color-mix(in srgb, var(--accent-a) 8%, transparent)",marginBottom:20,fontSize:12,fontWeight:500,color:C.text}}>
                <span className="grad-text" style={{fontWeight:700}}>
                  {isRTL?`تجربة مجانية ${trialLabelAr}`:`${trialLabel} free trial`}
                </span>
                <span style={{color:C.muted}}>·</span>
                <span>{isRTL?"لا حاجة لبيانات الدفع":"No credit card required"}</span>
                <span style={{color:C.muted}}>·</span>
                <span>{isRTL?"ألغِ في أي وقت":"Cancel anytime"}</span>
              </div>
            );
          })()}
          {/* Billing cycle toggle — only shown when both cycles are available */}
          {allowedCycles==="both"&&pkgs.some(p=>(p.priceMonthly||0)>0||(p.priceYearly||0)>0)&&(
            <div style={{display:"inline-flex",background:"rgba(255,255,255,.06)",borderRadius:30,padding:3,gap:2,marginBottom:4}}>
              {["monthly","yearly"].map(c=>(
                <button key={c} type="button" onClick={()=>setBillingCycle(c)}
                  style={{padding:"7px 20px",borderRadius:26,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,transition:"all .15s",
                    background:billingCycle===c?accent:"transparent",
                    color:billingCycle===c?"#1a1714":"rgba(255,255,255,.5)"}}>
                  {c==="monthly"?(isRTL?"شهري":"Monthly"):(isRTL?"سنوي":"Annual")}
                  {c==="yearly"&&<span style={{marginInlineStart:6,fontSize:10,background:"rgba(255,255,255,.12)",color:"rgba(255,255,255,.7)",padding:"1px 7px",borderRadius:10,fontWeight:700}}>{isRTL?"وفّر":"SAVE"}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-pkg-grid" style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(pkgs.length,3)},1fr)`,gap:16}}>
          {pkgs.map(pkg=>{
            const sel=selectedPkg===pkg.id;
            const locked=!!pkg.inviteOnly;
            const moduleList=pkg.modules||[];
            const priceVal=billingCycle==="yearly"?(pkg.priceYearly||0):(pkg.priceMonthly||0);
            const altPrice=billingCycle==="yearly"?(pkg.priceMonthly||0):(pkg.priceYearly||0);
            const hasPricing=(pkg.priceMonthly||0)>0||(pkg.priceYearly||0)>0;
            const showYearlySaving=billingCycle==="yearly"&&(pkg.priceMonthly||0)>0&&(pkg.priceYearly||0)>0;
            return(
              <div key={pkg.id} onClick={()=>{if(!locked)setSelectedPkg(pkg.id);}}
                style={{background:sel?"rgba(255,255,255,.08)":"rgba(255,255,255,.03)",border:`1.5px solid ${locked?"rgba(120,80,200,.35)":sel?accent:"rgba(255,255,255,.1)"}`,borderRadius:12,padding:"24px 20px",cursor:locked?"default":"pointer",transition:"all .18s",position:"relative",outline:sel?`2px solid ${accent}40`:"none",opacity:locked?.7:1}}>
                {pkg.highlight&&!locked&&<div style={{position:"absolute",top:-11,left:"50%",transform:"translateX(-50%)",background:accent,color:"#1a1714",fontSize:10,fontWeight:700,padding:"2px 12px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace",letterSpacing:".06em",whiteSpace:"nowrap"}}>{t.mostPopular}</div>}
                {locked&&<div style={{position:"absolute",top:-11,left:"50%",transform:"translateX(-50%)",background:"#7850c8",color:"white",fontSize:10,fontWeight:700,padding:"2px 12px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace",letterSpacing:".06em",whiteSpace:"nowrap"}}>{t.byInvitation}</div>}
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  {locked
                    ?<div style={{width:20,height:20,borderRadius:"50%",border:"2px solid rgba(120,80,200,.5)",background:"rgba(120,80,200,.12)",flexShrink:0}}/>
                    :<div style={{width:20,height:20,borderRadius:"50%",border:`2px solid ${sel?accent:"rgba(255,255,255,.25)"}`,background:sel?accent:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {sel&&<div style={{width:8,height:8,borderRadius:"50%",background:"#1a1714"}}/>}
                    </div>
                  }
                  <div style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:20,color:locked?"rgba(180,150,240,.8)":sel?accent:"white",fontStyle:"italic"}}>{pkg.name}</div>
                </div>
                {hasPricing?(
                  <div style={{marginBottom:8}}>
                    <span style={{fontSize:22,fontWeight:700,color:sel?accent:"white",fontFamily:"'JetBrains Mono',monospace"}}>{priceVal===0?(isRTL?"مجاني":"Free"):`${priceVal} SAR`}</span>
                    {priceVal>0&&<span style={{fontSize:12,color:"rgba(255,255,255,.4)",marginInlineStart:4}}>/ {billingCycle==="yearly"?(isRTL?"سنة":"year"):(isRTL?"شهر":"month")}</span>}
                    {showYearlySaving&&(pkg.priceMonthly*12-pkg.priceYearly)>0&&(
                      <div style={{fontSize:11,color:"#7be8a0",marginTop:2,fontFamily:"'JetBrains Mono',monospace"}}>
                        {isRTL?`وفّر ${pkg.priceMonthly*12-pkg.priceYearly} ر.س`:`Save ${pkg.priceMonthly*12-pkg.priceYearly} SAR vs monthly`}
                      </div>
                    )}
                  </div>
                ):pkg.price?(
                  <div style={{fontSize:13,color:accent,fontWeight:600,marginBottom:6,fontFamily:"'JetBrains Mono',monospace"}}>{pkg.price}</div>
                ):null}
                <div style={{fontSize:13,color:"rgba(255,255,255,.5)",lineHeight:1.6,marginBottom:pkg.showModules!==false&&moduleList.length?16:0}}>{pkg.description}</div>
                {pkg.showModules!==false&&moduleList.length>0&&(
                  <div style={{display:"flex",flexWrap:"wrap",gap:"4px 6px"}}>
                    {moduleList.map(m=>(
                      <span key={m} style={{fontSize:10,padding:"2px 8px",borderRadius:20,background:"rgba(255,255,255,.07)",color:"rgba(255,255,255,.55)",border:"1px solid rgba(255,255,255,.1)",fontFamily:"'JetBrains Mono',monospace"}}>{(platform.platformModuleNames||{})[m]||m}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{textAlign:"center",marginTop:32}}>
          <button className={tc(theme,"p-btn-accent-dark")} style={{padding:"12px 40px",fontSize:14,opacity:selectedPkg?1:.45,pointerEvents:selectedPkg?"all":"none"}}
            onClick={()=>selectedPkg&&setStep("form")}>
            {isRTL?"متابعة":"Continue"}
          </button>
          {!selectedPkg&&<div style={{fontSize:12,color:C.subtle,marginTop:8}}>{isRTL?"اختر باقة للمتابعة":"Select a package to continue"}</div>}
        </div>
      </div>
    </div>
  );

  /* ── Payment step ── */
  if(step==="payment"){
    const selPkg=(platform.platformPackages||[]).find(p=>p.id===selectedPkg)||{};
    const pubKey=(platform.platformApiKeys||{}).moyasarPublishableKey||"";
    const priceVal=billingCycle==="yearly"?(selPkg.priceYearly||0):(selPkg.priceMonthly||0);
    const effectivePrice=promoValid?0:priceVal;
    const amountHalalas=Math.round(effectivePrice*100);
    /* Server-validated promo check function */
    const validatePromo=async()=>{
      if(!(form.promoCode||"").trim()){setPromoErr("");setPromoValid(null);return;}
      setPromoErr("");
      try{
        const r=await authFetch("/api/validate-promo",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({code:form.promoCode.trim()})});
        const d=await r.json();
        if(d.ok){setPromoValid({trialDays:d.trialDays,label:d.label});setPromoErr("");}
        else{setPromoValid(null);setPromoErr(d.error||(isRTL?"رمز ترويجي غير صالح":"Invalid promo code."));}
      }catch{setPromoErr(isRTL?"خطأ في الشبكة. حاول مجدداً":"Network error. Please try again.");}
    };
    return(
      <div className={shellCls} style={{minHeight:"100vh",...shellFont,overflowY:"auto",position:"relative"}}>
        
        {accentCss&&<style>{accentCss}</style>}
        <div className={tc(theme,"p-dark-blob")}/>
        {themedNav}
        <div style={{maxWidth:480,margin:"0 auto",padding:"48px 24px 64px",position:"relative",zIndex:1}}>
          <button onClick={()=>setStep("form")} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",marginBottom:24,padding:0,display:"flex",alignItems:"center",gap:6}}>{isRTL?"رجوع":"Back"}</button>
          <div style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:26,fontStyle:"italic",fontWeight:300,color:C.text,marginBottom:6}}>{isRTL?"الدفع":"Complete payment"}</div>
          <div style={{fontSize:13,color:C.muted,marginBottom:16}}>
            {selPkg.name} · <span style={{fontFamily:"'JetBrains Mono',monospace",color:accent}}>{priceVal} SAR</span> / {billingCycle==="yearly"?(isRTL?"سنة":"year"):(isRTL?"شهر":"month")}
          </div>
          {/* Promo code input — always visible, server-validated */}
          <div style={{marginBottom:20,background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.1)",borderRadius:12,padding:"16px 18px"}}>
            <div style={{fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>{isRTL?"رمز ترويجي":"Have a promo code?"}</div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input
                className={tc(theme,"p-inp-dark")}
                value={form.promoCode||""}
                onChange={e=>{sf("promoCode",e.target.value.toUpperCase());setPromoValid(null);setPromoErr("");}}
                placeholder={isRTL?"أدخل الرمز الترويجي":"Enter promo code"}
                style={{flex:1,fontSize:13,textTransform:"uppercase",letterSpacing:2,fontFamily:"'JetBrains Mono',monospace"}}/>
              {(form.promoCode||"").trim()&&!promoValid&&(
                <button onClick={validatePromo} style={{background:accent,color:theme==="dark"?"#08080f":"white",border:"none",borderRadius:8,padding:"10px 14px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{isRTL?"تطبيق":"Apply"}</button>
              )}
            </div>
            {promoValid&&(
              <div style={{fontSize:12,color:"#7be8a0",marginTop:6,fontWeight:600}}>
                ✓ {isRTL?"تم تطبيق العرض":"Promo applied"}
              </div>
            )}
            {promoErr&&(
              <div style={{fontSize:12,color:"#D94F3D",marginTop:6,fontWeight:600}}>{promoErr}</div>
            )}
          </div>
          {/* If promo covers full cost, bypass payment */}
          {promoValid?(
            <div style={{background:theme==="dark"?"rgba(123,232,160,.08)":"#F0FDF4",border:"1px solid "+(theme==="dark"?"rgba(123,232,160,.2)":"#BBF7D0"),borderRadius:12,padding:24,textAlign:"center"}}>
              <div style={{fontSize:15,color:theme==="dark"?"#7be8a0":"#065F46",fontWeight:600,marginBottom:8}}>{isRTL?"لا يلزم الدفع":"No payment required"}</div>
              <div style={{fontSize:13,color:C.muted,marginBottom:20}}>{isRTL?"الرمز الترويجي يغطي اشتراكك للفترة الترويجية":"Your promo code covers this subscription for the promotional period."}</div>
              <button className={tc(theme,"p-btn-accent-dark")} style={{padding:"11px 32px",fontSize:14}} onClick={()=>afterPayment("")}>
                {isRTL?"تفعيل الاشتراك":"Activate subscription"}
              </button>
            </div>
          ):(
            <div style={{background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.1)",borderRadius:12,padding:24}}>
              {pubKey?(
                <>
                  <div id="moyasar-payment-form" className="mysr-form"/>
                  <MoyasarPaymentInit
                    amount={amountHalalas}
                    description={`${selPkg.name} subscription`}
                    pubKey={pubKey}
                    onPaid={afterPayment}
                    callbackUrl={paymentCallbackUrl}/>
                </>
              ):(
                <div style={{textAlign:"center",padding:"16px 0"}}>
                  <div style={{fontSize:14,color:"rgba(255,255,255,.5)",marginBottom:8}}>{isRTL?"بوابة الدفع غير مُعدّة":"Payment gateway not configured"}</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,.3)",lineHeight:1.6}}>{isRTL?"يجب على المسؤول إضافة مفتاح Moyasar في إعدادات المفاتيح":"The platform administrator needs to add a Moyasar publishable key in the API Keys settings."}</div>
                </div>
              )}
            </div>
          )}
          <div style={{fontSize:11,color:"rgba(255,255,255,.3)",marginTop:16,textAlign:"center",lineHeight:1.6}}>
            {isRTL?"مؤمّن بواسطة Moyasar · mada · Visa · Mastercard · Apple Pay · STC Pay":"Secured by Moyasar · mada · Visa · Mastercard · Apple Pay · STC Pay"}
          </div>
        </div>
      </div>
    );
  }

  /* ── Email verification step ── */
  /* ── Creating workspace transition screen ── */
  if(step==="creating") return(
    <div className={shellCls} style={{...shellFont,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
      
      {accentCss&&<style>{accentCss}</style>}
      <div className={tc(theme,"p-dark-blob")}/>
      {themedNav}
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"24px"}}>
        <div style={{textAlign:"center",maxWidth:400}}>
          <div style={{width:48,height:48,border:`3px solid ${accent}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 24px"}}/>
          <div style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:24,color:C.text,fontStyle:"italic",marginBottom:10}}>{isRTL?"جاري إنشاء مساحة العمل...":"Creating your workspace..."}</div>
          <div style={{fontSize:14,color:C.muted,lineHeight:1.7}}>{isRTL?"يرجى الانتظار بينما نجهز كل شيء لك":"Please wait while we set everything up for you"}</div>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if(step==="pending") return(
    <div className={shellCls} style={shellFont}>
      
      {accentCss&&<style>{accentCss}</style>}
      <div className={tc(theme,"p-dark-blob")}/>
      {themedNav}
      <div className={tc(theme,"p-dark-wrap")} style={{maxWidth:460,textAlign:"center",paddingTop:80}}>
        <div className={tc(theme,"p-dark-card")}>
          <div style={{fontSize:44,marginBottom:18}}>⏳</div>
          <div style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:24,color:C.text,fontStyle:"italic",marginBottom:10}}>{t.requestSubmitted}</div>
          <div style={{fontSize:14,color:C.muted,lineHeight:1.7,marginBottom:24}}>{t.pendingBody}</div>
          <button className={tc(theme,"p-btn-ghost-dark")} onClick={onBack}>{t.back}</button>
        </div>
      </div>
    </div>
  );

  if(step==="done"){
    /* Auto-enter workspace after a brief success flash */
    setTimeout(()=>{if(onEnterWorkspace)onEnterWorkspace();},1500);
    return(
      <div className={shellCls} style={shellFont}>
        
        {accentCss&&<style>{accentCss}</style>}
        <div className={tc(theme,"p-dark-blob")}/>
        {themedNav}
        <div className={tc(theme,"p-dark-wrap")} style={{maxWidth:460,textAlign:"center",paddingTop:80}}>
          <div className={tc(theme,"p-dark-card")}>
            <div style={{fontSize:44,marginBottom:18}}>✦</div>
            <div style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:24,color:C.text,fontStyle:"italic",marginBottom:10}}>{t.wsCreated}</div>
            <div style={{fontSize:14,color:C.muted,lineHeight:1.7,marginBottom:24}}>
              {isRTL?"جاري الدخول...":"Entering your workspace..."}
            </div>
            <div style={{width:32,height:32,border:`3px solid ${accent}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto"}}/>
          </div>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  /* (wizard helpers moved above step returns for mobile access) */

  return(
    <div className={shellCls} style={{...shellFont,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
      {vt.Toast}
      
      {accentCss&&<style>{accentCss}</style>}
      <div className={tc(theme,"p-dark-blob")}/>
      {themedNav}
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"120px 24px 24px",position:"relative",zIndex:1}}>
        <div style={{maxWidth:440,width:"100%"}}>
          {/* Progress bar */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,marginBottom:8}}>
            {Array.from({length:totalSteps}).map((_,i)=>(
              <div key={i} style={{height:4,borderRadius:2,flex:1,maxWidth:48,background:i<formStep?"var(--accent-grad)":(theme==="dark"?"rgba(255,255,255,.12)":"#E8E5E0"),boxShadow:i<formStep?"0 1px 6px -2px color-mix(in srgb, var(--accent-a) 45%, transparent)":"none",transition:"background .25s, box-shadow .25s"}}/>
            ))}
            <span style={{fontSize:12,color:C.muted,marginInlineStart:8,fontFamily:"'SF Mono',ui-monospace,Menlo,Monaco,monospace",letterSpacing:"0.08em"}}>{formStep}/{totalSteps}</span>
          </div>

          <div style={{marginBottom:32,marginTop:24}}>
            <h2 style={{fontSize:26,fontWeight:600,color:C.text,marginBottom:6,letterSpacing:"-0.01em"}}>{wizStepTitles[formStep-1]}</h2>
            <p style={{fontSize:14,color:C.muted}}>{wizStepSubs[formStep-1]}</p>
          </div>

          {err&&<div className={tc(theme,"p-err-dark")} style={{marginBottom:16}}>{err}</div>}

          {/* Step 1: Account + Profile with inline verification */}
          {formStep===1&&(<>
            <div style={{marginBottom:12,position:"relative"}}>
              <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("user")}</div>
              <input className={tc(theme,"p-inp-dark")} value={form.name} onChange={e=>sf("name",e.target.value)} placeholder={isRTL?"الاسم الكامل":"Full name"} style={inpStyle} autoFocus/>
            </div>
            {/* Email + verify */}
            <div style={{marginBottom:12}}>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{flex:1,position:"relative"}}>
                  <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("email")}</div>
                  <input className={tc(theme,"p-inp-dark")} type="email" value={form.email} onChange={e=>{sf("email",e.target.value);setEmailVerified(false);}} placeholder={isRTL?"البريد الإلكتروني":"Email address"} style={inpStyle} disabled={emailVerified}/>
                </div>
                {requireEmailVerify&&!emailVerified&&!verifySending&&form.email.includes("@")&&(
                  <button onClick={async()=>{setVerifyErr("");setVerifySending(true);try{const r=await authFetch("/api/send-verification",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email:form.email.trim()})});const d=await r.json();if(!d.ok)setVerifyErr(d.error||"Failed");setVerifySending(false);}catch{setVerifyErr("Network error");setVerifySending(false);}}}
                    style={{background:accent,color:theme==="dark"?"#08080f":"white",border:"none",borderRadius:8,padding:"10px 14px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{isRTL?"تحقق":"Verify"}</button>
                )}
                {requireEmailVerify&&emailVerified&&<span style={{color:"#2D7D52",fontSize:13,fontWeight:700,whiteSpace:"nowrap"}}>&#10003; {isRTL?"تم":"Verified"}</span>}
              </div>
              {verifySending&&<div style={{fontSize:11,color:accent,marginTop:4}}>{isRTL?"جاري إرسال الرمز...":"Sending code..."}</div>}
              {requireEmailVerify&&!emailVerified&&form.email.includes("@")&&!verifySending&&verifyErr&&<div style={{fontSize:11,color:"#D94F3D",marginTop:4}}>{verifyErr}</div>}
              {requireEmailVerify&&!emailVerified&&!verifySending&&form.email.includes("@")&&(
                <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center"}}>
                  <input className={tc(theme,"p-inp-dark")} value={verifyCode} onChange={e=>setVerifyCode(e.target.value.replace(/\D/g,""))} maxLength={6} placeholder={isRTL?"رمز التحقق":"6-digit code"} style={{flex:1,fontSize:13,padding:"8px 12px",textAlign:"center",letterSpacing:4,fontFamily:"'JetBrains Mono',monospace"}}/>
                  {verifyCode.length>=4&&(
                    <button onClick={submitVerifyEmail} style={{background:accent,color:theme==="dark"?"#08080f":"white",border:"none",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{isRTL?"تأكيد":"Confirm"}</button>
                  )}
                </div>
              )}
            </div>
            {/* Phone + verify */}
            <div style={{marginBottom:12}}>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <CountryCodePicker value={form.phoneCode} onChange={v=>sf("phoneCode",v)} theme={theme}/>
                <input className={tc(theme,"p-inp-dark")} type="tel" value={form.phoneNumber} onChange={e=>{sf("phoneNumber",e.target.value.replace(/\D/g,""));setPhoneVerified(false);}} placeholder={isRTL?"رقم الهاتف":"Phone number"} style={{flex:1}} disabled={phoneVerified}/>
                {requirePhoneVerify&&!phoneVerified&&form.phoneNumber.length>=7&&(
                  <button onClick={async()=>{setVerifyErr("");try{const r=await authFetch("/api/verify-phone",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({phone:form.phoneCode+form.phoneNumber.trim()})});const d=await r.json();if(!d.ok)setVerifyErr(d.error||"Failed to send code.");}catch{setVerifyErr("Network error.");}}}
                    style={{background:accent,color:theme==="dark"?"#08080f":"white",border:"none",borderRadius:8,padding:"10px 14px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{isRTL?"تحقق":"Verify"}</button>
                )}
                {requirePhoneVerify&&phoneVerified&&<span style={{color:"#2D7D52",fontSize:13,fontWeight:700,whiteSpace:"nowrap"}}>&#10003; {isRTL?"تم":"Verified"}</span>}
              </div>
              {requirePhoneVerify&&!phoneVerified&&form.phoneNumber.length>=7&&(
                <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center"}}>
                  <input className={tc(theme,"p-inp-dark")} value={verifyPhoneCode} onChange={e=>setVerifyPhoneCode(e.target.value.replace(/\D/g,""))} maxLength={6} placeholder={isRTL?"رمز الواتساب":"WhatsApp code"} style={{flex:1,fontSize:13,padding:"8px 12px",textAlign:"center",letterSpacing:4,fontFamily:"'JetBrains Mono',monospace"}}/>
                  {verifyPhoneCode.length>=4&&(
                    <button onClick={submitVerifyPhone} style={{background:accent,color:theme==="dark"?"#08080f":"white",border:"none",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{isRTL?"تأكيد":"Confirm"}</button>
                  )}
                </div>
              )}
            </div>
            <div style={{marginBottom:12,position:"relative"}}>
              <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("lock")}</div>
              <input className={tc(theme,"p-inp-dark")} type="password" value={form.password} onChange={e=>sf("password",e.target.value)} placeholder={isRTL?"كلمة المرور":"Password"} style={inpStyle}/>
            </div>
            <div style={{marginBottom:16,position:"relative"}}>
              <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("lock")}</div>
              <input className={tc(theme,"p-inp-dark")} type="password" value={form.confirmPassword||""} onChange={e=>sf("confirmPassword",e.target.value)} placeholder={isRTL?"تأكيد كلمة المرور":"Confirm password"} style={inpStyle} onKeyDown={e=>e.key==="Enter"&&nextFormStep()}/>
            </div>
            {verifyErr&&<div style={{fontSize:11,color:"#D94F3D",marginBottom:8}}>{verifyErr}</div>}
          </>)}

          {/* Step 2: Workspace Details + Household */}
          {formStep===2&&(<>
            <div style={{marginBottom:12,position:"relative"}}>
              <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("building")}</div>
              <input className={tc(theme,"p-inp-dark")} value={form.workspaceName} onChange={e=>sf("workspaceName",e.target.value)} placeholder={isRTL?"اسم مساحة العمل":"Workspace name"} style={inpStyle} autoFocus/>
            </div>
            {/* Household details */}
            <div style={{marginTop:12,paddingTop:14,borderTop:`1px solid ${theme==="dark"?"rgba(255,255,255,.08)":"#E8E5E0"}`}}>
              <div style={{fontSize:11,fontWeight:600,color:C.muted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:10}}>{isRTL?"تفاصيل المنزل":"Household Details"}</div>
              <div style={{display:"flex",gap:8,marginBottom:12}}>
                <PlatformSearchSelect value={form.country} onChange={v=>{sf("country",v);sf("city","");}} options={COUNTRIES.map(c=>({value:c,label:c}))} placeholder={isRTL?"الدولة":"Country"} theme={theme} accent={accent}/>
                <PlatformSearchSelect value={form.city} onChange={v=>sf("city",v)} options={(CITIES_BY_COUNTRY[form.country]||[]).map(c=>({value:c,label:c}))} placeholder={isRTL?"المدينة":"City"} theme={theme} accent={accent}/>
              </div>
              <div style={{marginBottom:12}}>
                <PlatformSearchSelect value={form.propertyType} onChange={v=>sf("propertyType",v)} options={PROPERTY_TYPES} placeholder={isRTL?"نوع العقار":"Property type"} theme={theme} accent={accent}/>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:12}}>
                <div style={{flex:1}}>
                  <label style={{fontSize:10,color:C.muted,marginBottom:4,display:"block"}}>{isRTL?"عدد المقيمين":"Residents"}</label>
                  <PlatformSearchSelect value={String(form.residents||1)} onChange={v=>sf("residents",parseInt(v)||1)} options={Array.from({length:20},(_,i)=>({value:String(i+1),label:String(i+1)}))} placeholder={isRTL?"عدد":"Count"} theme={theme} accent={accent}/>
                </div>
                <div style={{flex:1}}>
                  <label style={{fontSize:10,color:C.muted,marginBottom:4,display:"block"}}>{isRTL?"عدد الموظفين":"Staff members"}</label>
                  <PlatformSearchSelect value={String(form.staffCount||0)} onChange={v=>sf("staffCount",parseInt(v)||0)} options={Array.from({length:21},(_,i)=>({value:String(i),label:String(i)}))} placeholder={isRTL?"عدد":"Count"} theme={theme} accent={accent}/>
                </div>
              </div>
            </div>
            {/* Selected plan + compare */}
            {selPkgObj&&pkgs.length>1&&(
              <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
                <span style={{fontSize:12,color:C.muted}}>{isRTL?"الخطة:":"Plan:"}</span>
                <span className="grad-text" style={{fontSize:12,fontWeight:700,letterSpacing:"0.01em"}}>{selPkgObj.name}</span>
                <button onClick={()=>setShowComparePlans(true)} style={{fontSize:11,color:"var(--accent-a)",background:"none",border:"none",cursor:"pointer",textDecoration:"underline",textUnderlineOffset:3}}>{isRTL?"قارن الخطط":"Compare plans"}</button>
              </div>
            )}
          </>)}

          {/* Step 3: Invite code (if needed) */}
          {formStep===3&&(<>
            <div style={{marginBottom:20,position:"relative"}}>
              <div style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>{inputIcon("key")}</div>
              <input className={tc(theme,"p-inp-dark")} value={inviteCode} onChange={e=>setInviteCode(e.target.value)} placeholder="XXXXXX" style={{...inpStyle,textTransform:"uppercase",letterSpacing:4,fontFamily:"'JetBrains Mono',monospace"}} autoFocus onKeyDown={e=>e.key==="Enter"&&nextFormStep()}/>
            </div>
          </>)}

          {/* Continue button */}
          <button className={tc(theme,"p-btn-primary-dark")} onClick={nextFormStep} disabled={creating} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:10,marginBottom:12,marginTop:8,opacity:creating?0.7:1}}>
            {creating?(isRTL?"جاري إنشاء مساحة العمل...":"Creating workspace…"):formStep<totalSteps?(isRTL?"متابعة":"Continue"):(verifySending?(isRTL?"جاري الإرسال...":"Sending..."):(platform.signupMode==="approval"?t.requestWs:(isRTL?"إنشاء مساحة العمل":"Create workspace")))}
          </button>

          {/* Back link */}
          {formStep>1&&(
            <button onClick={()=>{setErr("");setFormStep(formStep-1);}} style={{background:"none",border:"none",color:C.muted,fontSize:13,cursor:"pointer",padding:"8px 0",width:"100%",textAlign:"center"}}>{isRTL?"رجوع":"Back"}</button>
          )}
        </div>
      </div>

      {/* ── Compare Plans Popup ── */}
      {showComparePlans&&(
        <div onClick={()=>setShowComparePlans(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div onClick={e=>e.stopPropagation()} style={{maxWidth:700,width:"100%",maxHeight:"80vh",overflowY:"auto",background:theme==="dark"?"#161618":"white",borderRadius:16,padding:28,position:"relative"}}>
            <button onClick={()=>setShowComparePlans(false)} style={{position:"absolute",top:12,right:12,background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer"}}>x</button>
            <h3 style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:22,fontStyle:"italic",fontWeight:300,color:C.text,marginBottom:20}}>{isRTL?"قارن الخطط":"Compare Plans"}</h3>
            <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(pkgs.length,3)},1fr)`,gap:12}}>
              {pkgs.filter(p=>!p.inviteOnly).map(pkg=>{
                const isSel=selectedPkg===pkg.id;
                const priceVal=billingCycle==="yearly"?(pkg.priceYearly||0):(pkg.priceMonthly||0);
                const curr=pkg.currency||"SAR";const sym={SAR:"SAR",USD:"$",EUR:"\u20AC",GBP:"\u00A3",AED:"AED"}[curr]||curr;
                return(
                  <div key={pkg.id} style={{border:`1.5px solid ${isSel?accent:(theme==="dark"?"rgba(255,255,255,.1)":"#E8E5E0")}`,borderRadius:12,padding:16,cursor:"pointer",background:isSel?(accent+"10"):"transparent"}} onClick={()=>{setSelectedPkg(pkg.id);setShowComparePlans(false);}}>
                    <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:4}}>{pkg.name}</div>
                    <div style={{fontSize:12,color:C.muted,marginBottom:8,minHeight:36}}>{pkg.description}</div>
                    {priceVal>0&&<div style={{fontSize:18,fontWeight:700,color:C.text,fontFamily:"'JetBrains Mono',monospace",marginBottom:8}}>{["USD","EUR","GBP"].includes(curr)?`${sym}${priceVal}`:`${priceVal} ${sym}`}<span style={{fontSize:11,fontWeight:400,color:C.muted}}>/{billingCycle==="yearly"?"yr":"mo"}</span></div>}
                    {(pkg.modules||[]).map(m=>(
                      <div key={m} style={{fontSize:11,color:C.muted,padding:"2px 0"}}><span style={{color:accent}}>&#10003;</span> {(platform.platformModuleNames||{})[m]||m}</div>
                    ))}
                    {isSel&&<div style={{fontSize:11,color:accent,fontWeight:700,marginTop:8}}>{isRTL?"الخطة الحالية":"Current plan"}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

